import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../landing.css";
import { LANDING_FAQS } from "../data/housemindFaq";

const NAV_ITEMS = [
  { id: "features", label: "Features" },
  { id: "how", label: "How it works" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" }
];

export default function LandingPage() {
  const navRef = useRef(null);
  const [activeNav, setActiveNav] = useState("");

  useEffect(() => {
    const navSections = NAV_ITEMS.map((item) => ({
      id: item.id,
      el: document.getElementById(item.id)
    })).filter((item) => item.el);

    const syncActiveFromHash = () => {
      const hashId = window.location.hash.replace("#", "");
      if (NAV_ITEMS.some((item) => item.id === hashId)) {
        setActiveNav(hashId);
      }
    };

    const onScroll = () => {
      if (navRef.current) {
        navRef.current.classList.toggle("lp-scrolled", window.scrollY > 20);
      }

      const current = navSections.reduce((active, item) => {
        const top = item.el.getBoundingClientRect().top;
        return top <= 150 ? item.id : active;
      }, "");
      setActiveNav(current);
    };
    window.addEventListener("scroll", onScroll);
    window.addEventListener("hashchange", syncActiveFromHash);
    onScroll();
    syncActiveFromHash();

    // Reveal on scroll
    const reveals = document.querySelectorAll(".lp-reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add("lp-visible"), i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));

    // FAQ accordion
    const faqBtns = document.querySelectorAll(".lp-faq-q");
    const handleFaq = (e) => {
      const item = e.currentTarget.parentElement;
      const isOpen = item.classList.contains("lp-open");
      document.querySelectorAll(".lp-faq-item.lp-open").forEach((el) => el.classList.remove("lp-open"));
      if (!isOpen) item.classList.add("lp-open");
    };
    faqBtns.forEach((btn) => btn.addEventListener("click", handleFaq));

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("hashchange", syncActiveFromHash);
      observer.disconnect();
      faqBtns.forEach((btn) => btn.removeEventListener("click", handleFaq));
    };
  }, []);

  return (
    <div className="lp-root">
      {/* NAV */}
      <nav className="lp-nav" ref={navRef}>
        <a href="#top" className="lp-nav-logo">House<em>Mind</em></a>
        <div className="lp-nav-links">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={activeNav === item.id ? "lp-nav-link-active" : ""}
              aria-current={activeNav === item.id ? "page" : undefined}
              onClick={() => setActiveNav(item.id)}
            >
              {item.label}
            </a>
          ))}
        </div>
        <Link to="/register" className="lp-nav-cta">Start free →</Link>
      </nav>

      {/* HERO */}
      <section className="lp-hero" id="top">
        <div className="lp-hero-content">
          <div className="lp-hero-label">Your household, handled</div>
          <h1>
            One calm place<br/>for everything<br/>your home{" "}
            <em>
              <span className="lp-hero-em-line">forgets —</span>{" "}
              <span className="lp-hero-em-line">and the money</span>{" "}
              <span className="lp-hero-em-line">it wastes.</span>
            </em>
          </h1>
          <p className="lp-hero-sub">
            HouseMind connects food, money, property, maintenance, pets, safety, plans, and records — so your
            household can see what matters and act before it becomes urgent.
          </p>
          <div className="lp-hero-actions">
            <Link to="/register" className="lp-btn-primary">Start for free <span>→</span></Link>
            <a href="#how" className="lp-btn-secondary">See how it works <span>↓</span></a>
          </div>
          <div className="lp-hero-value">Most users save $50-$200/month within the first 30 days.</div>
          <div className="lp-hero-trust">
            <div className="lp-trust-item"><div className="lp-trust-dot"></div>Free forever plan</div>
            <div className="lp-trust-item"><div className="lp-trust-dot"></div>No credit card needed</div>
            <div className="lp-trust-item"><div className="lp-trust-dot"></div>Private by default</div>
            <div className="lp-trust-item"><div className="lp-trust-dot"></div>Never miss a birthday</div>
          </div>
        </div>

        <div className="lp-hero-visual">
          <div className="lp-float-card lp-float-card-1">
            <div className="lp-fc-label">Saved this year</div>
            <div className="lp-fc-value">$347</div>
            <div className="lp-fc-sub">via HouseMind insights</div>
          </div>
          <div className="lp-hero-card-main">
            <div className="lp-hc-header">
              <div className="lp-hc-logo">House<em>Mind</em></div>
              <div className="lp-hc-date">Sunday, May 3</div>
            </div>
            <div className="lp-briefing-box">
              <div className="lp-briefing-label">✦ Daily briefing</div>
              "Good morning John. Sally's birthday is in 6 days — no gift selected yet.
              Your Honda oil change is overdue by 200 miles. And your HVAC warranty expires in 18 days."
            </div>
            <div className="lp-alert-items">
              <div className="lp-alert-item">
                <div className="lp-ai-icon">🎁</div>
                <div className="lp-ai-text"><strong>Sally's birthday</strong> · 6 days away · no gift yet</div>
                <div className="lp-ai-badge lp-badge-urgent">Urgent</div>
              </div>
              <div className="lp-alert-item">
                <div className="lp-ai-icon">🚗</div>
                <div className="lp-ai-text"><strong>Honda oil change</strong> · 200 miles overdue</div>
                <div className="lp-ai-badge lp-badge-soon">Soon</div>
              </div>
              <div className="lp-alert-item">
                <div className="lp-ai-icon">💰</div>
                <div className="lp-ai-text"><strong>Savings found</strong> · Switch to Spotify Family, save $59/yr</div>
                <div className="lp-ai-badge lp-badge-save">Save</div>
              </div>
            </div>
            <div className="lp-module-pills">
              <div className="lp-pill">🍽 Food</div>
              <div className="lp-pill">🎁 Gifts</div>
              <div className="lp-pill">🏠 Home Vault</div>
              <div className="lp-pill">🚗 Vehicles</div>
              <div className="lp-pill">⚡ Utilities</div>
              <div className="lp-pill">🔄 Subs</div>
              <div className="lp-pill">🐾 Pets</div>
              <div className="lp-pill">🧰 Home Care</div>
              <div className="lp-pill">💵 Budget</div>
            </div>
          </div>
          <div className="lp-float-card lp-float-card-2">
            <div className="lp-fc-label">Active alerts</div>
            <div className="lp-fc-value" style={{ color: "var(--rose)" }}>3</div>
            <div className="lp-fc-sub">need your attention</div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <div className="lp-social-proof">
        <div className="lp-sp-item"><div className="lp-sp-icon">🏠</div><div><div className="lp-sp-num">12,400+</div>households managed</div></div>
        <div className="lp-sp-item"><div className="lp-sp-icon">💰</div><div><div className="lp-sp-num">$50-$200</div>monthly savings found</div></div>
        <div className="lp-sp-item"><div className="lp-sp-icon">⭐</div><div><div className="lp-sp-num">4.9/5</div>average rating</div></div>
        <div className="lp-sp-item"><div className="lp-sp-icon">🔒</div><div><div className="lp-sp-num">100%</div>private by default</div></div>
      </div>

      {/* PROBLEM */}
      <section className="lp-problem">
        <div className="lp-section-label">The problem</div>
        <h2 className="lp-section-title">
          Your home is leaking <em>time, money,</em><br/>and attention.
        </h2>
        <div className="lp-problem-grid">
          <div className="lp-problem-item lp-reveal">
            <div className="lp-prob-icon">🤯</div>
            <div className="lp-prob-title">Too many apps doing too little</div>
            <div className="lp-prob-body">One app for groceries. Another for warranties. Another for car maintenance. None of them talk to each other. None of them know your family.</div>
          </div>
          <div className="lp-problem-item lp-reveal">
            <div className="lp-prob-icon">💸</div>
            <div className="lp-prob-title">Money leaking everywhere</div>
            <div className="lp-prob-body">The average household wastes $219/month on subscriptions they forgot about, warranties that expired, and repairs that could have been prevented.</div>
          </div>
          <div className="lp-problem-item lp-reveal">
            <div className="lp-prob-icon">😰</div>
            <div className="lp-prob-title">Things fall through the cracks</div>
            <div className="lp-prob-body">Car registration expired. Warranty ran out the week before the appliance broke. Mom's birthday became a last-minute gift that felt last-minute. Sound familiar?</div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="features" className="lp-section">
        <div className="lp-section-label">Everything included</div>
        <h2 className="lp-section-title">Your whole household.<br/>One <em>operating system.</em></h2>
        <p className="lp-section-sub">
          Everything your household needs, connected and intelligent. Each module is powerful alone — together they're extraordinary.
        </p>
        <div className="lp-modules-grid">
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#4A7C59" }}>
            <div className="lp-mc-icon">🍽</div>
            <div className="lp-mc-title">Food</div>
            <div className="lp-mc-body">AI meal planning, pantry tracking, expiry alerts, and smart shopping lists organized by store section.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">7-day AI meal plans</div>
              <div className="lp-mc-feat">Expiry alerts before food goes bad</div>
              <div className="lp-mc-feat">Shopping list by aisle</div>
              <div className="lp-mc-feat">Recipe suggestions from what you have</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#C25B45" }}>
            <div className="lp-mc-icon">🎁</div>
            <div className="lp-mc-title">People &amp; Gifts</div>
            <div className="lp-mc-body">Never miss a birthday or settle for a bad last-minute gift. HouseMind remembers the people you love and suggests gifts that actually fit them.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Birthday and occasion countdowns before panic mode</div>
              <div className="lp-mc-feat">Gift ideas based on their taste, age, and past notes</div>
              <div className="lp-mc-feat">Shareable gift registries</div>
              <div className="lp-mc-feat">Card messages that sound personal, not generic</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#2A7B6F" }}>
            <div className="lp-mc-icon">🏠</div>
            <div className="lp-mc-title">Home Vault</div>
            <div className="lp-mc-body">All your warranties, appliance manuals, home systems, and receipts — organized, searchable, and never lost.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Warranty expiry alerts (30 days notice)</div>
              <div className="lp-mc-feat">"Ask your appliance" AI</div>
              <div className="lp-mc-feat">Home systems maintenance schedule</div>
              <div className="lp-mc-feat">Recall alerts by model number</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#1D5A51" }}>
            <div className="lp-mc-icon">🚗</div>
            <div className="lp-mc-title">Vehicles</div>
            <div className="lp-mc-body">Every vehicle in your household, tracked. Maintenance schedules, service logs, insurance, and the AI quote checker.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Know when your car needs service — and never overpay again</div>
              <div className="lp-mc-feat">"Is this quote fair?" AI check</div>
              <div className="lp-mc-feat">Insurance &amp; registration alerts</div>
              <div className="lp-mc-feat">Fuel tracking &amp; MPG trends</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#C97B2E" }}>
            <div className="lp-mc-icon">🔄</div>
            <div className="lp-mc-title">Subscriptions</div>
            <div className="lp-mc-body">Every recurring charge, visible and analyzed. HouseMind finds the waste and gives you the script to fix it.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Stop getting charged for things you forgot</div>
              <div className="lp-mc-feat">AI monthly waste report</div>
              <div className="lp-mc-feat">Cancellation script generator</div>
              <div className="lp-mc-feat">Duplicate subscription detection</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#0EA5A3" }}>
            <div className="lp-mc-icon">⚡</div>
            <div className="lp-mc-title">Utilities</div>
            <div className="lp-mc-body">Track electric, water, gas, internet, solar, and home service bills so HouseMind can catch due dates, spikes, and savings opportunities.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Monthly bill and usage history</div>
              <div className="lp-mc-feat">Spike detection compared with prior bills</div>
              <div className="lp-mc-feat">Trip utility mode reminders</div>
              <div className="lp-mc-feat">AI utility savings assistant</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#6366F1" }}>
            <div className="lp-mc-icon">✈</div>
            <div className="lp-mc-title">Events &amp; Trips</div>
            <div className="lp-mc-body">Plan trips, coordinate events, and get AI-powered packing lists and activity recommendations for any destination.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">AI packing list by destination</div>
              <div className="lp-mc-feat">Activity &amp; restaurant recommendations</div>
              <div className="lp-mc-feat">Group trip coordination</div>
              <div className="lp-mc-feat">Day-by-day itinerary builder</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#8A4162" }}>
            <div className="lp-mc-icon">🐾</div>
            <div className="lp-mc-title">Pets</div>
            <div className="lp-mc-body">Run daily care, health records, food supplies, vet preparation, insurance claims, and safe sharing from one pet profile.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Daily feeding, medication, walk, and care routines</div>
              <div className="lp-mc-feat">Health passport and veterinary record scans</div>
              <div className="lp-mc-feat">Pet-food stock, expiry, and shopping</div>
              <div className="lp-mc-feat">Caregiver, vet, and lost-pet sharing tools</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#347A65" }}>
            <div className="lp-mc-icon">🧰</div>
            <div className="lp-mc-title">Home Care</div>
            <div className="lp-mc-body">One actionable place for household maintenance and repairs generated from the records and schedules you confirm.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Due, upcoming, and long-range work</div>
              <div className="lp-mc-feat">Recurring maintenance schedules</div>
              <div className="lp-mc-feat">Providers, cost, notes, and completion history</div>
              <div className="lp-mc-feat">Connected Vault, pet-care, and safety work</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#346A8A" }}>
            <div className="lp-mc-icon">🏡</div>
            <div className="lp-mc-title">Property Profile</div>
            <div className="lp-mc-body">Keep the facts about the physical home, its spaces, access details, insurance, and property-specific context together.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Owner and renter property facts</div>
              <div className="lp-mc-feat">Rooms, systems, access, and service context</div>
              <div className="lp-mc-feat">Home policy tracking and document scan</div>
              <div className="lp-mc-feat">Canonical location for local weather</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#B5483F" }}>
            <div className="lp-mc-icon">🛟</div>
            <div className="lp-mc-title">Emergency &amp; Safety</div>
            <div className="lp-mc-body">Make emergency contacts, household plans, critical controls, supplies, and readiness checks findable when they matter.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Guided readiness checklist</div>
              <div className="lp-mc-feat">Contacts, meeting points, exits, and controls</div>
              <div className="lp-mc-feat">Recurring drills and resource verification</div>
              <div className="lp-mc-feat">Focused offline-ready emergency view</div>
            </div>
          </div>
          <div className="lp-module-card lp-reveal" style={{ "--mc": "#A26A22" }}>
            <div className="lp-mc-icon">💵</div>
            <div className="lp-mc-title">Household Budget</div>
            <div className="lp-mc-body">See planned and recorded household spending together, without connecting HouseMind directly to your bank account.</div>
            <div className="lp-mc-features">
              <div className="lp-mc-feat">Monthly category budgets and actuals</div>
              <div className="lp-mc-feat">Bills, subscriptions, groceries, gifts, and care costs</div>
              <div className="lp-mc-feat">Budget pressure and variance signals</div>
              <div className="lp-mc-feat">Money overview across household modules</div>
            </div>
          </div>
        </div>
        <p className="lp-section-sub">
          Timeline is the shared chronological view across these areas, bringing due dates, activity, and verified household records into one place.
        </p>
      </section>

      {/* INTELLIGENCE STRIP */}
      <div className="lp-intel-strip">
        <div className="lp-intel-pulse">
          <span className="lp-intel-dot"></span>
          <span className="lp-intel-label">Nightly intelligence</span>
        </div>
        <p className="lp-intel-text">
          HouseMind evaluates signals across food, property, maintenance, pets, safety, money, vehicles, subscriptions, occasions, and trips — then surfaces useful actions without turning every record into an alert.
        </p>
        <div className="lp-intel-modules">
          <span>🍽 Food</span>
          <span>⚡ Utilities</span>
          <span>🎁 Gifts</span>
          <span>🏠 Home Vault</span>
          <span>🚗 Vehicles</span>
          <span>🔄 Subs</span>
          <span>✈ Trips</span>
          <span>🐾 Pets</span>
          <span>🧰 Home Care</span>
          <span>🛟 Safety</span>
          <span>💵 Budget</span>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="lp-how" id="how">
        <div className="lp-section-label">How it works</div>
        <h2 className="lp-section-title">Set up in minutes.<br/><em>Useful immediately.</em></h2>
        <p className="lp-section-sub">
          No long onboarding. No overwhelming setup wizard. Add your first item and HouseMind starts working for you right away.
        </p>
        <div className="lp-steps">
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">1</div>
            <div className="lp-step-content">
              <div className="lp-step-title">Create your account</div>
              <div className="lp-step-body">Free forever. No credit card. Your data is private by default from day one.</div>
            </div>
          </div>
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">2</div>
            <div className="lp-step-content">
              <div className="lp-step-title">Add your household</div>
              <div className="lp-step-body">Add what matters first: people, pets, property, appliances, bills, vehicles, or a safety plan. One useful item at a time.</div>
            </div>
          </div>
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">3</div>
            <div className="lp-step-content">
              <div className="lp-step-title">Wake up to your briefing</div>
              <div className="lp-step-body">Every night the intelligence engine scans all your modules and queues your morning briefing — surfacing what matters before you think to ask.</div>
            </div>
          </div>
          <div className="lp-step lp-reveal">
            <div className="lp-step-num">4</div>
            <div className="lp-step-content">
              <div className="lp-step-title">Act in one tap</div>
              <div className="lp-step-body">Every alert has a quick action. Book a service, find a gift, view a warranty — without leaving the dashboard.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CROSS MODULE */}
      <section className="lp-cross-module">
        <div className="lp-section-label">Connected intelligence</div>
        <h2 className="lp-section-title">The insights no single app<br/>can give you — <em>until now.</em></h2>
        <p className="lp-section-sub">
          Because HouseMind knows your whole household, it finds connections other apps can't see.
        </p>
        <div className="lp-insights-list">
          <div className="lp-insight lp-reveal">
            <div className="lp-insight-icon">🚗💳</div>
            <div className="lp-insight-text">
              <strong>"You're paying $12.99/mo for AAA roadside assistance.</strong> Your vehicle insurance already includes roadside coverage. You may be paying twice — check your policy."
            </div>
          </div>
          <div className="lp-insight lp-reveal">
            <div className="lp-insight-icon">🎁🏠</div>
            <div className="lp-insight-text">
              <strong>"Sofia just got a new laptop for her birthday.</strong> Add it to Home Vault so HouseMind can track the warranty and remind you before coverage ends."
            </div>
          </div>
          <div className="lp-insight lp-reveal">
            <div className="lp-insight-icon">👨‍👩‍👧🎵</div>
            <div className="lp-insight-text">
              <strong>"You and Sally are both paying for Spotify individually ($10.99 each).</strong> Spotify Family covers up to 6 people for $16.99/mo. Switch and save $59 this year."
            </div>
          </div>
          <div className="lp-insight lp-reveal">
            <div className="lp-insight-icon">⚡✈</div>
            <div className="lp-insight-text">
              <strong>"Your trip starts tomorrow.</strong> Set thermostat eco mode, pause sprinklers, and check the water heater schedule before you leave."
            </div>
          </div>
          <div className="lp-insight lp-reveal">
            <div className="lp-insight-icon">🍽✈</div>
            <div className="lp-insight-text">
              <strong>"Your Cancun trip starts in 9 days.</strong> Your meal plan this week uses up your perishables. Your pantry will be mostly clear when you leave — and we'll help you restock when you return."
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section className="lp-section">
        <div className="lp-privacy-grid">
          <div>
            <div className="lp-section-label">Privacy first</div>
            <h2 className="lp-section-title">Private by default.<br/><em>Share by choice.</em></h2>
            <p className="lp-section-sub">
              Every member of your household gets their own complete account. Nothing is shared unless you explicitly choose to share it.
            </p>
            <div className="lp-privacy-points">
              <div className="lp-privacy-point lp-reveal">
                <div className="lp-pp-icon">🔒</div>
                <div>
                  <div className="lp-pp-title">Your data stays yours</div>
                  <div className="lp-pp-body">Every account is fully independent. Connect with household members without merging your data.</div>
                </div>
              </div>
              <div className="lp-privacy-point lp-reveal">
                <div className="lp-pp-icon">👁</div>
                <div>
                  <div className="lp-pp-title">Share exactly what you want</div>
                  <div className="lp-pp-body">Share a grocery list, recipe, trip plan, or person profile. Vehicles, records, subscriptions, and utilities stay private.</div>
                </div>
              </div>
              <div className="lp-privacy-point lp-reveal">
                <div className="lp-pp-icon">🎁</div>
                <div>
                  <div className="lp-pp-title">Gift planning stays private</div>
                  <div className="lp-pp-body">Planning a gift for Sally? Keep those ideas in your own account and share only the person details you choose.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lp-privacy-visual lp-reveal">
            <div className="lp-pv-title">John's sharing settings</div>
            <div className="lp-share-item"><span className="lp-share-name">🛒 Grocery list</span><span className="lp-share-toggle lp-tog-on">Shared with Sally</span></div>
            <div className="lp-share-item"><span className="lp-share-name">🍝 Pasta recipe</span><span className="lp-share-toggle lp-tog-on">Shared with Sally</span></div>
            <div className="lp-share-item"><span className="lp-share-name">✈️ Cancun trip plan</span><span className="lp-share-toggle lp-tog-on">Shared with household</span></div>
            <div className="lp-share-item"><span className="lp-share-name">🎁 Sally's birthday gift ideas</span><span className="lp-share-toggle lp-tog-off">Private — hidden from Sally</span></div>
            <div className="lp-share-item"><span className="lp-share-name">🚗 My personal car</span><span className="lp-share-toggle lp-tog-off">Private</span></div>
            <div className="lp-share-item"><span className="lp-share-name">💳 Personal subscriptions</span><span className="lp-share-toggle lp-tog-off">Private</span></div>
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="lp-payoff">
        <div className="lp-section-label">Fast ROI</div>
        <div className="lp-payoff-header">
          <h2 className="lp-section-title">This pays for<br/><em>itself fast.</em></h2>
          <p className="lp-section-sub">
            HouseMind is built around the household money leaks people actually feel: forgotten renewals,
            missed warranty windows, food waste, and repairs that should have been caught sooner.
          </p>
        </div>
        <div className="lp-payoff-grid">
          <div className="lp-payoff-card lp-reveal">
            <div className="lp-payoff-kicker">Cancel 1 forgotten subscription</div>
            <div className="lp-payoff-value">$10-$20/month saved</div>
          </div>
          <div className="lp-payoff-card lp-reveal">
            <div className="lp-payoff-kicker">Catch 1 warranty before it expires</div>
            <div className="lp-payoff-value">$200-$1,000 saved</div>
          </div>
          <div className="lp-payoff-card lp-reveal">
            <div className="lp-payoff-kicker">Avoid throwing out food you forgot</div>
            <div className="lp-payoff-value">$50/month saved</div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lp-pricing-section">
        <div style={{ textAlign: "center" }}>
          <div className="lp-section-label" style={{ justifyContent: "center" }}>Pricing</div>
          <h2 className="lp-section-title">Simple pricing.<br/><em>Pays for itself.</em></h2>
          <p className="lp-section-sub" style={{ margin: "0 auto" }}>
            HouseMind users find an average of $400 in annual savings. At $9/month, it pays for itself in the first week.
          </p>
        </div>
        <div className="lp-pricing-grid">
          <div className="lp-price-card lp-reveal">
            <div className="lp-pc-plan">Free</div>
            <div className="lp-pc-price">$0</div>
            <div className="lp-pc-period">forever · no credit card</div>
            <div className="lp-pc-tagline">Everything you need to get started. No time limit.</div>
            <div className="lp-pc-features">
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>5 people · 5 vault items · 1 vehicle</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Basic renewal &amp; expiry reminders</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>3-day meal plans</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>5 subscriptions tracked</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Daily AI briefing</span></div>
            </div>
            <Link to="/register" className="lp-pc-cta lp-pc-cta-outline">Start free →</Link>
          </div>
          <div className="lp-price-card lp-featured lp-reveal">
            <div className="lp-featured-badge">Most popular</div>
            <div className="lp-pc-plan">Pro</div>
            <div className="lp-pc-price">$9</div>
            <div className="lp-pc-period">per month · cancel anytime</div>
            <div className="lp-pc-tagline">Unlimited everything. Full AI across every module.</div>
            <div className="lp-pc-features">
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Everything in Free, unlimited</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Receipt &amp; document scanning (OCR)</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Full intelligence across household areas</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>"Is this quote fair?" vehicle AI</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>AI waste report · cancellation scripts</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Cross-module intelligence</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Home Vault PDF export</span></div>
            </div>
            <Link to="/register" className="lp-pc-cta lp-pc-cta-filled">Start Pro free →</Link>
          </div>
          <div className="lp-price-card lp-reveal">
            <div className="lp-pc-plan">Family</div>
            <div className="lp-pc-price">$15</div>
            <div className="lp-pc-period">per month · up to 3 accounts</div>
            <div className="lp-pc-tagline">Every member gets their own full account. Privacy kept.</div>
            <div className="lp-pc-features">
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Everything in Pro for all members</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Family sharing for selected food, people, trips, and pets</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Property, records, vehicles, subscriptions, and utilities stay private</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Household duplicate detection</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Family occasion calendar</span></div>
              <div className="lp-pc-feat"><div className="lp-pc-feat-check">✓</div><span>Group trip planning</span></div>
            </div>
            <Link to="/register" className="lp-pc-cta lp-pc-cta-outline">Start family plan →</Link>
          </div>
        </div>
        <p style={{ textAlign: "center", marginTop: "32px", fontSize: "13px", color: "var(--ink-muted)" }}>
          All plans include a 14-day Pro trial. No credit card required to start.
        </p>
      </section>

      {/* TESTIMONIALS */}
      <section className="lp-testimonials">
        <div className="lp-section-label">What people say</div>
        <h2 className="lp-section-title">Real households.<br/><em>Real results.</em></h2>
        <div className="lp-testi-grid">
          <div className="lp-testi-card lp-reveal">
            <div className="lp-testi-stars">★★★★★</div>
            <div className="lp-testi-quote">"HouseMind found $340 in subscription waste in the first week. The Spotify family plan thing alone saved us $59. I've already made back 3 years of Pro subscription."</div>
            <div className="lp-testi-author">
              <div className="lp-testi-avatar">MR</div>
              <div><div className="lp-testi-name">Marcus R.</div><div className="lp-testi-role">Homeowner · Las Vegas, NV</div></div>
            </div>
          </div>
          <div className="lp-testi-card lp-reveal">
            <div className="lp-testi-stars">★★★★★</div>
            <div className="lp-testi-quote">"The daily briefing is the first thing I check every morning. It told me my dryer warranty was expiring — the dryer broke 3 weeks later. I filed the claim. $800 saved."</div>
            <div className="lp-testi-author">
              <div className="lp-testi-avatar" style={{ background: "var(--rose)" }}>JL</div>
              <div><div className="lp-testi-name">Jennifer L.</div><div className="lp-testi-role">Mom of 3 · Austin, TX</div></div>
            </div>
          </div>
          <div className="lp-testi-card lp-reveal">
            <div className="lp-testi-stars">★★★★★</div>
            <div className="lp-testi-quote">"I showed my mechanic the 'is this quote fair?' result and the price dropped $180 instantly. The vehicle module alone is worth the subscription ten times over."</div>
            <div className="lp-testi-author">
              <div className="lp-testi-avatar" style={{ background: "var(--amber)" }}>DK</div>
              <div><div className="lp-testi-name">David K.</div><div className="lp-testi-role">Family of 4 · Chicago, IL</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="lp-section">
        <div className="lp-section-label">Questions</div>
        <h2 className="lp-section-title">Everything you<br/>want to <em>know.</em></h2>
        <div className="lp-faq-list">
          {LANDING_FAQS.map(item => (
            <div key={item.question} className="lp-faq-item">
              <button className="lp-faq-q">{item.question}<span className="lp-faq-arrow">+</span></button>
              <div className="lp-faq-a">{item.answer}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-final-cta">
        <h2>Your household<br/>deserves to be <em>calm.</em></h2>
        <p>Join thousands of households that finally have one place for everything.</p>
        <div className="lp-btn-group">
          <Link to="/register" className="lp-btn-teal">Start free today →</Link>
          <a href="#features" className="lp-btn-ghost-white">Explore features</a>
        </div>
        <div className="lp-final-note">No credit card required · Free forever plan · Cancel Pro anytime</div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-logo">House<em>Mind</em></div>
        <div className="lp-footer-links">
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/terms">Terms of service</Link>
          <a href="mailto:support@housemindos.com">Contact</a>
          <Link to="/guide">Guide</Link>
        </div>
        <div className="lp-footer-copy">© 2026 HouseMind. All rights reserved.</div>
      </footer>
    </div>
  );
}
